<script setup lang="ts">
import { computed, ref } from 'vue'
import { useCall, type CallTile } from '../stores/call'
import Avatar from './Avatar.vue'
import TrackView from './TrackView.vue'

const props = defineProps<{ tile: CallTile; large?: boolean }>()
const call = useCall()
const root = ref<HTMLElement | null>(null)

const m = computed(() => props.tile.member)
const screen = computed(() => props.tile.kind === 'screen')
const pinned = computed(() => call.focused?.key === props.tile.key)
// Screens are letterboxed so nothing is cropped; so is a camera shown large.
const speaking = computed(() => !screen.value && m.value.speaking && m.value.micOn)
const fit = computed(() => (screen.value || props.large ? 'contain' : 'cover'))

function fullscreen(): void {
  if (document.fullscreenElement) void document.exitFullscreen()
  else void root.value?.requestFullscreen?.()
}
</script>

<template>
  <div
    ref="root"
    class="group relative flex h-full w-full cursor-pointer items-center justify-center overflow-hidden bg-bg-primary select-none"
    :data-speaking="speaking || undefined"
    :data-testid="screen ? 'call-screen-tile' : 'call-member'"
    :data-name="m.name"
    :data-pinned="pinned || undefined"
    @click="call.togglePin(tile.key)"
    @dblclick.stop="fullscreen"
  >
    <TrackView v-if="tile.track" :track="tile.track" :mirror="m.local && !screen" :fit="fit" />
    <Avatar v-else :id="m.userId" :name="m.name" :size="large ? 96 : 48" />

    <span class="mono absolute bottom-0 left-0 flex items-center gap-2 bg-black/70 px-1.5 py-0.5 text-white">
      <span v-if="screen" class="text-white/60 uppercase">Screen</span>
      <!-- Voice: bars dance while this person talks, lie flat while they're quiet. -->
      <svg
        v-else-if="m.micOn"
        class="wave h-2.5 w-3.5 shrink-0"
        :class="speaking ? 'text-voice' : 'text-white/40'"
        :data-active="speaking || undefined"
        viewBox="0 0 14 10"
        aria-hidden="true"
        data-testid="call-wave"
      >
        <rect v-for="i in 5" :key="i" :x="(i - 1) * 3" y="0" width="2" height="10" fill="currentColor" />
      </svg>
      <span>{{ m.name }}{{ m.local ? ' (you)' : '' }}</span>
      <span v-if="!screen && !m.micOn" class="text-signal uppercase" data-testid="call-muted">Muted</span>
    </span>

    <div
      class="absolute top-0 right-0 z-20 flex gap-px opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100"
    >
      <button
        class="mono bg-black/70 px-1.5 py-0.5 text-white uppercase hover:bg-white hover:text-black"
        :aria-pressed="pinned"
        :title="pinned ? 'Unpin' : 'Pin: show large'"
        data-testid="tile-pin"
        @click.stop="call.togglePin(tile.key)"
      >
        {{ pinned ? 'Unpin' : 'Pin' }}
      </button>
      <button
        class="mono bg-black/70 px-1.5 py-0.5 text-white uppercase hover:bg-white hover:text-black"
        title="Full screen"
        data-testid="tile-fullscreen"
        @click.stop="fullscreen"
      >
        Full
      </button>
    </div>
  </div>
</template>

<style scoped>
.wave rect {
  transform-box: fill-box;
  transform-origin: center;
  transform: scaleY(0.2);
  transition: transform 0.3s;
}
.wave[data-active] rect {
  animation: wave 0.9s ease-in-out infinite;
}
.wave[data-active] rect:nth-child(2) {
  animation-delay: -0.45s;
}
.wave[data-active] rect:nth-child(3) {
  animation-delay: -0.2s;
}
.wave[data-active] rect:nth-child(4) {
  animation-delay: -0.65s;
}
.wave[data-active] rect:nth-child(5) {
  animation-delay: -0.3s;
}
@keyframes wave {
  0%,
  100% {
    transform: scaleY(0.25);
  }
  50% {
    transform: scaleY(1);
  }
}
@media (prefers-reduced-motion: reduce) {
  .wave[data-active] rect {
    animation: none;
    transform: scaleY(0.7);
  }
}
</style>
